/**
 * ThemeContext — the React half of Scrivar's dark mode.
 *
 * Owns the user's choice (`system` | `light` | `dark`), keeps it in
 * localStorage, and keeps the `dark` class on <html> in sync. While the choice
 * is `system` it subscribes to the OS `prefers-color-scheme` media query so the
 * app follows a mid-session OS switch; the subscription is torn down as soon as
 * the user picks an explicit theme (and on unmount).
 *
 * It is NOT responsible for the first paint — the inline boot script in
 * index.html sets the class before React ever mounts, so there is no flash.
 * This provider re-derives the same value and takes over from there.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  THEMES,
  getStoredTheme,
  storeTheme,
  resolveIsDark,
  applyThemeClass,
  getDarkMediaQuery,
  prefersDark,
} from '@/lib/theme';

const ThemeContext = createContext(null);

// Used when a component renders outside the provider (isolated unit tests,
// the static marketing pages' React islands). Inert rather than throwing.
const INERT = { theme: 'system', setTheme() {}, themes: THEMES };

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);

  useEffect(() => {
    // getDarkMediaQuery() returns null where matchMedia is unavailable
    // (node-env tests, ancient shells) — those resolve as "not dark".
    // prefersDark() is the same guarded OS-preference read (mirrors the same
    // matchMedia-absent → false fallback) as a direct `mq.matches` check.
    const mq = getDarkMediaQuery();
    applyThemeClass(resolveIsDark(theme, prefersDark()));

    // Only 'system' cares about later OS changes.
    if (theme !== 'system' || !mq) return undefined;

    const onChange = (event) => {
      applyThemeClass(resolveIsDark('system', event ? event.matches : mq.matches));
    };

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    if (typeof mq.addListener === 'function') {
      // Safari < 14 / older WebViews.
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
    return undefined;
  }, [theme]);

  const setTheme = useCallback((next) => {
    // storeTheme normalises anything unexpected to 'system'; the effect above
    // then re-applies the class for the (possibly unchanged) resolved value.
    setThemeState(storeTheme(next));
  }, []);

  const value = useMemo(() => ({ theme, setTheme, themes: THEMES }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** @returns {{ theme: 'system'|'light'|'dark', setTheme: (t: string) => void, themes: string[] }} */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Otherwise-silent inert fallback — flag it in dev so a missing
    // <ThemeProvider> (a real mistake) doesn't read as "theme controls just
    // don't do anything" with no clue why. Fine to fire on every call.
    if (import.meta.env?.DEV) {
      console.warn('useTheme used outside ThemeProvider — theme controls will be inert');
    }
    return INERT;
  }
  return ctx;
}

export default ThemeContext;
